import { Link, useNavigate } from 'react-router-dom'
import {Search, Award, ShieldCheck, TrendingUp, GraduationCap, Building2, Users, ArrowRight, CheckCircle2, } from 'lucide-react'
import Navbar from '@/components/Navbar'
import Footer from '@/components/Footer'

const HERO_IMAGE = 'https://static.lumi.new/83/838d6f90dbc89741eba9f4cc8ec605ca.png'

const stats = [
  { value: '+500', label: 'Colaboradores activos' },
  { value: '+200', label: 'Empresas registradas' },
  { value: '+200', label: 'Vacantes publicadas' },
  { value: '94%', label: 'Satisfacción' },
]

const features = [
  {
    icon: Search,
    title: 'Bolsa de empleo inteligente',
    desc: 'Publica y encuentra vacantes con filtros por salario, ciudad, modalidad y giro empresarial.',
  },
  {
    icon: Award,
    title: 'Reputación laboral verificable',
    desc: 'Historial de evaluaciones, reconocimientos y desempeño que acompaña a cada colaborador.',
  },
  {
    icon: GraduationCap,
    title: 'Capacitación continua',
    desc: 'Cursos, talleres y certificaciones que se integran al perfil profesional automáticamente.',
  },
  {
    icon: ShieldCheck,
    title: 'Reportes laborales validados',
    desc: 'Procesos transparentes de revisión para reportar conductas graves de forma confiable.',
  },
  {
    icon: TrendingUp,
    title: 'Indicadores de desempeño',
    desc: 'Promedios por colaborador, área y tendencias para decisiones de talento basadas en datos.',
  },
  {
    icon: Users,
    title: 'Vinculación empresa-colaborador',
    desc: 'Relaciones laborales dentro de la plataforma que habilitan evaluaciones e historiales.',
  },
]

const audiences = [
  {
    icon: Users,
    tag: 'Para colaboradores',
    title: 'Construye tu carrera profesional',
    points: ['Perfil profesional puntuado', 'Postúlate a vacantes ideales', 'Acumula certificaciones', 'Historial laboral verificable'],
    cta: 'Crear perfil',
  },
  {
    icon: Building2,
    tag: 'Para empresas',
    title: 'Encuentra y desarrolla talento',
    points: ['Publica vacantes ilimitadas', 'Evalúa a tus colaboradores', 'Reportes de desempeño', 'Reputación corporativa'],
    cta: 'Registrar empresa',
  },
]

function Home() {
  const navigate = useNavigate()
  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden bg-slate-950">
        <div className="absolute inset-0">
          <img src={HERO_IMAGE} alt="Profesionales colaborando" className="h-full w-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/85 to-slate-950/40" />
        </div>
        <div className="relative mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8 lg:py-32">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-2 rounded-full border border-amber-400/30 bg-amber-400/10 px-3 py-1 text-xs font-semibold text-amber-300">
              Empleabilidad · Capacitación · Reputación laboral
            </span>
            <h1 className="mt-6 text-4xl font-bold leading-tight tracking-tight text-white sm:text-5xl lg:text-6xl">
              Conecta con oportunidades y personas en las que <span className="text-amber-400">puedes confiar</span>
            </h1>
            <p className="mt-6 text-lg leading-relaxed text-slate-300">
              El futuro del trabajo está aquí. Con Beeznz Work construyes tu trayectoria profesional,
              fortaleces tu perfil y conectas con empresas que buscan talento real.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Link
                to="/registro"
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-amber-400 px-6 py-3 text-sm font-semibold text-slate-900 transition-colors hover:bg-amber-300"
              >
                Comenzar ahora <ArrowRight size={18} />
              </Link>
              <button
                onClick={() => navigate('/login', { state: { from: '/empleos' } })}
                className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-600 px-6 py-3 text-sm font-semibold text-white transition-colors hover:border-slate-400"
              >
                Explorar empleos
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="border-b border-slate-100 bg-white">
        <div className="mx-auto grid max-w-7xl grid-cols-2 gap-8 px-4 py-12 sm:px-6 lg:grid-cols-4 lg:px-8">
          {stats.map((stat) => (
            <div key={stat.label} className="text-center">
              <p className="text-3xl font-bold text-slate-900 sm:text-4xl">{stat.value}</p>
              <p className="mt-1 text-sm text-slate-500">{stat.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section className="bg-slate-50 py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              Un ecosistema laboral completo
            </h2>
            <p className="mt-4 text-lg text-slate-600">
              Mucho más que buscar empleo: una comunidad donde el talento, la confianza y las oportunidades crecen juntos.
            </p>
          </div>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {features.map((feature) => (
              <div
                key={feature.title}
                className="rounded-2xl border border-slate-200 bg-white p-6 transition-shadow hover:shadow-lg"
              >
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-amber-50 text-amber-600">
                  <feature.icon size={22} />
                </div>
                <h3 className="mt-5 text-lg font-semibold text-slate-900">{feature.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Audiences */}
      <section className="bg-white py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-8 lg:grid-cols-2">
            {audiences.map((aud) => (
              <div
                key={aud.tag}
                className="flex flex-col rounded-3xl border border-slate-200 bg-gradient-to-br from-white to-slate-50 p-8"
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-slate-900 text-amber-400">
                  <aud.icon size={24} />
                </div>
                <span className="mt-6 text-xs font-semibold uppercase tracking-wider text-amber-600">{aud.tag}</span>
                <h3 className="mt-2 text-2xl font-bold text-slate-900">{aud.title}</h3>
                <ul className="mt-6 space-y-3">
                  {aud.points.map((point) => (
                    <li key={point} className="flex items-center gap-3 text-sm text-slate-700">
                      <CheckCircle2 size={18} className="shrink-0 text-amber-500" />
                      {point}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/registro"
                  className="mt-8 inline-flex w-fit items-center gap-2 rounded-lg bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-slate-800"
                >
                  {aud.cta} <ArrowRight size={16} />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-slate-950 py-20">
        <div className="mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">
            Impulsa la profesionalización laboral
          </h2>
          <p className="mt-4 text-lg text-slate-300">
            Forma parte de una nueva manera de vivir el trabajo: más oportunidades, mejores prácticas y un crecimiento profesional basado en confianza.
          </p>
          <Link
            to="/registro"
            className="mt-8 inline-flex items-center gap-2 rounded-lg bg-amber-400 px-7 py-3 text-sm font-semibold text-slate-900 transition-colors hover:bg-amber-300"
          >
            Crear cuenta gratis <ArrowRight size={18} />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  )
}

export default Home
